/**
 * AI asistent za pomoć (ADR-063) - pregled za podršku:
 * potrošnja ovog meseca (budžet), odakle stižu odgovori, odgovori sa 👎 i
 * kandidati za keš (odobreni odgovor se sledećim korisnicima vraća bez modela).
 * Glas (ADR-074, Faza 1): svoj budžet, ocene glas prema tekstu, ispravljeni
 * transkripti (sluh pogrešio) i trajanje odgovora.
 */
import { useCallback, useEffect, useState } from 'react';
import { Bot, Check, Mic, RefreshCw, ThumbsDown, X } from 'lucide-react';
import { api } from '../../api/client';

type Rating = { helpful: boolean | null; n: number };
type Voice = {
  enabled: boolean; pilotTenants: string[]; model: string;
  budget: { monthlyEur: number; tenantMonthlyEur: number; spentEur: number; requests: number };
  questions: number; notHeard: number; fixedTranscripts: number;
  ratings: Array<Rating & { channel: 'text' | 'voice' }>;
  latency: { n: number; p50Ms: number | null; p90Ms: number | null; sttMs: number | null; llmMs: number | null; ttsMs: number | null };
  topTenants: Array<{ tenantId: string; name: string | null; costEur: number; requests: number }>;
};
type Overview = {
  voice: Voice;
  month: string; aiEnabled: boolean; model: string;
  budget: { monthlyEur: number; tenantMonthlyEur: number; spentEur: number; requests: number };
  kb: { version: string; chunks: number };
  topTenants: Array<{ tenantId: string; name: string | null; costEur: number; requests: number }>;
  bySource: Array<{ source: string; n: number }>;
  ratings: Array<{ helpful: boolean | null; n: number }>;
};
type Negative = { id: string; tenantId: string; question: string; answer: string; source: string; channel?: 'text' | 'voice'; createdAt: string };
type CacheRow = { id: string; question: string; answer: string; screen: string; up: number; down: number; status: string };

const box: React.CSSProperties = { background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 16 };
const muted: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)' };
const small: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)', fontSize: 12, cursor: 'pointer' };
const SOURCE: Record<string, string> = { ai: 'Model', cache: 'Keš (bez troška)', refusal: 'Van teme (odbijeno)', limit: 'Granica/budžet' };

/** 👍 od ocenjenih, npr. „80 % (8/10)"; bez ocena „-". */
function liked(rows: Rating[]): string {
  const up = rows.filter((r) => r.helpful === true).reduce((s, r) => s + r.n, 0);
  const rated = rows.filter((r) => r.helpful !== null).reduce((s, r) => s + r.n, 0);
  return rated ? `${Math.round((up / rated) * 100)} % (${up}/${rated})` : '-';
}
const sec = (ms: number | null) => (ms === null ? '-' : `${(ms / 1000).toFixed(1)} s`);

function VoiceBox({ v }: { v: Voice }) {
  const pct = Math.min(100, (v.budget.spentEur / Math.max(0.01, v.budget.monthlyEur)) * 100);
  const pilot = v.pilotTenants.includes('*') ? 'sve firme' : `${v.pilotTenants.length} ${v.pilotTenants.length === 1 ? 'firma' : 'firme'}`;
  const fixedPct = v.questions ? Math.round((v.fixedTranscripts / v.questions) * 100) : 0;
  return (
    <div style={box} data-testid="assistant-voice">
      <h2 style={{ margin: '0 0 8px', fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }}><Mic size={14} /> Glas <span style={muted}>{v.enabled ? `(pilot: ${pilot} · ${v.model})` : '(ISKLJUČEN - nema ključa za glas ili model)'}</span></h2>
      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))' }}>
        <div>
          <div style={muted}>Budžet za glas</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>€{v.budget.spentEur.toFixed(2)} <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>/ €{v.budget.monthlyEur}</span></div>
          <div style={{ height: 6, background: 'var(--color-border)', borderRadius: 3, marginTop: 6 }}><div style={{ width: `${pct}%`, height: 6, borderRadius: 3, background: pct > 85 ? '#dc2626' : '#16a34a' }} /></div>
          <div style={{ ...muted, marginTop: 6 }}>po firmi najviše €{v.budget.tenantMonthlyEur}/mesec</div>
        </div>
        <div>
          <div style={muted}>Pitanja glasom</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{v.questions}</div>
          <div style={muted}>Nije se čulo (procena): {v.notHeard}</div>
          <div style={muted} title="Korisnik je kliknuo „Nije to? Ispravite tekst” i poslao ispravljeno pitanje">Ispravljen transkript: {v.fixedTranscripts}{v.questions ? ` (${fixedPct} %)` : ''}</div>
        </div>
        <div>
          <div style={muted}>👍 od ocenjenih</div>
          <div style={{ fontSize: 13 }}>Glas: <b>{liked(v.ratings.filter((r) => r.channel === 'voice'))}</b></div>
          <div style={{ fontSize: 13 }}>Tekst: <b>{liked(v.ratings.filter((r) => r.channel === 'text'))}</b></div>
        </div>
        <div>
          <div style={muted}>Trajanje odgovora na serveru ({v.latency.n})</div>
          <div style={{ fontSize: 13 }}>Medijana <b>{sec(v.latency.p50Ms)}</b> · 90 % ispod <b>{sec(v.latency.p90Ms)}</b></div>
          <div style={muted}>Sluh {sec(v.latency.sttMs)} · model {sec(v.latency.llmMs)} · glas {sec(v.latency.ttsMs)} (prosek)</div>
        </div>
      </div>
      {v.topTenants.length > 0 && (
        <div style={{ ...muted, marginTop: 10 }}>Najviše troše: {v.topTenants.map((t) => `${t.name ?? t.tenantId.slice(0, 8)} €${t.costEur.toFixed(3)} (${t.requests})`).join(' · ')}</div>
      )}
    </div>
  );
}

export function AssistantPage() {
  const [ov, setOv] = useState<Overview | null>(null);
  const [neg, setNeg] = useState<Negative[]>([]);
  const [cache, setCache] = useState<CacheRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [o, n, c] = await Promise.all([
        api.get<Overview>('/admin/assistant/overview'),
        api.get<Negative[]>('/admin/assistant/negative'),
        api.get<CacheRow[]>('/admin/assistant/cache?status=candidate'),
      ]);
      setOv(o); setNeg(n); setCache(c); setError(null);
    } catch (e) { setError((e as Error).message || 'Nije učitano.'); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const decide = async (id: string, action: 'approve' | 'reject') => {
    await api.post(`/admin/assistant/cache/${id}/${action}`, {});
    void load();
  };

  const pct = ov ? Math.min(100, (ov.budget.spentEur / Math.max(0.01, ov.budget.monthlyEur)) * 100) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Bot size={22} />
        <h1 style={{ margin: 0, fontSize: 22 }}>AI asistent za pomoć</h1>
        <div style={{ flex: 1 }} />
        <button style={small} onClick={() => void load()}><RefreshCw size={12} /> Osveži</button>
      </div>
      {error && <div style={{ color: '#dc2626' }}>{error}</div>}

      {ov && (
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))' }}>
          <div style={box}>
            <div style={muted}>Budžet {ov.month}</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>€{ov.budget.spentEur.toFixed(2)} <span style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>/ €{ov.budget.monthlyEur}</span></div>
            <div style={{ height: 6, background: 'var(--color-border)', borderRadius: 3, marginTop: 8 }}><div style={{ width: `${pct}%`, height: 6, borderRadius: 3, background: pct > 85 ? '#dc2626' : '#16a34a' }} /></div>
            <div style={{ ...muted, marginTop: 8 }}>{ov.budget.requests} poziva modela · po firmi najviše €{ov.budget.tenantMonthlyEur}/mesec</div>
            <div style={{ ...muted, marginTop: 4 }}>{ov.aiEnabled ? `Uključen · ${ov.model}` : 'ISKLJUČEN (nema ANTHROPIC_API_KEY) - pomoć radi bez modela'}</div>
          </div>
          <div style={box}>
            <div style={muted}>Odgovori ovog meseca</div>
            {ov.bySource.map((s) => <div key={s.source} style={{ fontSize: 14 }}>{SOURCE[s.source] ?? s.source}: <b>{s.n}</b></div>)}
            <div style={{ ...muted, marginTop: 6 }}>Ocene: {ov.ratings.map((r) => `${r.helpful === null ? 'bez ocene' : r.helpful ? '👍' : '👎'} ${r.n}`).join(' · ') || '-'}</div>
            <div style={{ ...muted, marginTop: 6 }}>Baza znanja: {ov.kb.chunks} delova · verzija {ov.kb.version}</div>
          </div>
          <div style={box}>
            <div style={muted}>Firme koje najviše troše</div>
            {ov.topTenants.length === 0 && <div style={muted}>-</div>}
            {ov.topTenants.map((t) => <div key={t.tenantId} style={{ fontSize: 13 }}>{t.name ?? t.tenantId.slice(0, 8)} - €{t.costEur.toFixed(3)} ({t.requests})</div>)}
          </div>
        </div>
      )}

      {ov && <VoiceBox v={ov.voice} />}

      <div style={box}>
        <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>Kandidati za keš <span style={muted}>(odobren odgovor se vraća bez poziva modela; 3 × 👍 bez 👎 odobrava sam)</span></h2>
        {cache.length === 0 && <div style={muted}>Nema kandidata.</div>}
        {cache.map((c) => (
          <div key={c.id} style={{ borderTop: '1px solid var(--color-border)', padding: '10px 0' }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{c.question} <span style={muted}>· {c.screen} · 👍 {c.up} 👎 {c.down}</span></div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', margin: '4px 0 6px' }}>{c.answer}</div>
            <button style={small} onClick={() => void decide(c.id, 'approve')}><Check size={12} /> Odobri</button>{' '}
            <button style={small} onClick={() => void decide(c.id, 'reject')}><X size={12} /> Odbij</button>
          </div>
        ))}
      </div>

      <div style={box}>
        <h2 style={{ margin: '0 0 8px', fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }}><ThumbsDown size={14} /> Odgovori sa 👎 <span style={muted}>(dopuniti uputstvo ili česta pitanja)</span></h2>
        {neg.length === 0 && <div style={muted}>Nema loše ocenjenih odgovora.</div>}
        {neg.map((n) => (
          <div key={n.id} style={{ borderTop: '1px solid var(--color-border)', padding: '10px 0' }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{n.question}</div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', margin: '4px 0' }}>{n.answer}</div>
            <div style={muted}>{new Date(n.createdAt).toLocaleString('sr-Latn-RS')} · {SOURCE[n.source] ?? n.source}{n.channel === 'voice' ? ' · glasom' : ''}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
